import { Box, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { API_POST, DBRequest, useConfig } from '../../api/api';
import useFetch from '../../api/useFetch';
import CardGrid from '../../components/cards/layout/CardGrid';
import CardContainer from '../../components/cards/layout/CardContainer';
import SkeletonCard from '../../components/cards/SkeletonCard';
import HtmlRenderer from '../../components/preview/HtmlRenderer';
import InfoGridCard from '../../components/cards/PictureNameCards/InfoGridCard';
import SpeedDialMenu from '../../components/menu/SpeedDialMenu';
import Feedback from '../../components/special/Feedback';
import Loader from '../../components/special/Loader';
import MailErrorInfo from '../../components/special/MailErrorInfo';
import { useEffect, useState } from 'react';

const MAIL_STATUS_FAILED = 2;

export const ExtendMail = ({ row, onUpdate, onDelete, onEdit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, setApiData } = useFetch(`maillogs/${row.id}`);

  const initialInfos = [
    {
      label: t('Receiver'),
      value: apiData?.data.receiver,
    },
  ];

  const errorInfo = [
    {
      label: t('Error'),
      value: (
        <MailErrorInfo
          errorMessage={apiData?.data?.errorMessage}
          variant="text"
        />
      ),
    },
  ];

  const [infos, setinfos] = useState(initialInfos);

  useEffect(() => {
    let newInfos = initialInfos;

    apiData?.data.cc?.forEach((cc, index) => {
      const label = index == 0 ? 'CC' : '';
      newInfos.push({ label: label, value: cc.address });
    });

    setinfos(newInfos);
  }, [apiData?.data]);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  // retry failed mail
  const [isRetrying, setIsRetrying] = useState(false);

  function handleRetry() {
    DBRequest({
      config,
      path: `mail-logs/${row.id}/retry`,
      method: API_POST(),
      onLoading: setIsRetrying,
      onResponse: handleRetrySuccess,
      onError: (error) =>
        handleAlertOpen(
          error?.message || t('Email could not be sent'),
          'error'
        ),
    });
  }

  function handleRetrySuccess(response) {
    const updatedMail = { ...apiData.data, ...response.data };
    setApiData({ ...apiData, data: updatedMail });
    onUpdate({
      ...row,
      status: updatedMail.status,
      errorMessage: updatedMail.errorMessage,
    });
    handleAlertOpen(t('Email queued for sending'), 'success');
  }

  const actions = [
    {
      icon: <ReplayIcon />,
      name: t('Retry sending email'),
      onClick: handleRetry,
      hidden: apiData?.data?.status !== MAIL_STATUS_FAILED,
    },
  ];

  return (
    <Box className="flex flex-col gap-4 relative overflow-y-auto">
      <Loader active={isRetrying} />
      <Feedback setState={setAlertState} state={alertState} />
      {!apiData ? (
        <MailSkeleton />
      ) : (
        <>
          <SpeedDialMenu actions={actions} />
          <CardGrid>
            <InfoGridCard span={2} infos={infos} />
            {apiData.data?.errorMessage && (
              <InfoGridCard span={2} infos={errorInfo} />
            )}

            <CardContainer
              span={999}
              sx={{ backgroundColor: 'white', height: '800px' }}
            >
              <HtmlRenderer htmlString={apiData.data.htmlMail} />
            </CardContainer>
          </CardGrid>
        </>
      )}
    </Box>
  );
};

const MailSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={52} span={4} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={191} />
      <SkeletonCard height={156} span={4} />
    </CardGrid>
  );
};
